import { fileToBase64, bufferToBlobPdf } from '@/utils/files'
import { equals, makeFilter, select, mergeFilters, decoratorValueArray } from '@/utils/dynamic'
import { api } from '../api'
import { ServiceQi } from '@/services/qi'
import { ServiceRFQ3s } from '@/services/RFQ3s'
import { ServiceContents, CONTENT_KEYS } from '@/services/contents'
import { ServiceInvoice } from '@/services/Invoice'
import { ServiceRFQs } from '@/services/RFQs'
import { ServiceSalesOrders } from '@/services/SalesOrders'
import { ServiceRFQ2s } from '@/services/RFQ2s'
import { ServiceRepairTearDowns } from '@/services/RepairTearDowns'
import { ServiceReplacementParts } from '@/services/replacement-parts'
import { generateInvoice3 } from '@/pdf-modules/invoice-3'
import { generateInvoice4 } from '@/pdf-modules/invoice-4'
import { generateInvoice5 } from '@/pdf-modules/invoice-5'
import { generateInvoice7 } from '@/pdf-modules/invoice-7'
import { generateInvoice8 } from '@/pdf-modules/invoice-8'
import { generateInvoice9 } from '@/pdf-modules/invoice-9'
import { generateInvoice10 } from '@/pdf-modules/invoice-10'
import { generateQi } from '@/pdf-modules/qi'
import { generateExchangeAgreement } from '@/pdf-modules/exchange-agreement'
import { generateOrderAcknowledgment } from '@/pdf-modules/order-acknowledgment'
import { generatePackingSlip } from '@/pdf-modules/packing-slip'
import { generatePo } from '@/pdf-modules/po'
import { generateProformaShipSupplier } from '@/pdf-modules/proforma-ship-supplier'
import { generateQuoteMultiParts } from '@/pdf-modules/quote-multi-parts'
import { generateTd } from '@/pdf-modules/td'
import { generateWo } from '@/pdf-modules/wo'

const API = 'PdfGenerators'

const API_PDF_GENERATORS = {
  GENERATE_PDF: `${API}/GetPDF`
}

class Service {
  async generatePdf ({ content, logoKey }) {
    const logoType = logoKey === 1 ? 'bax-technics-logo' : 'bel-airexpress-logo'
    const { data: pdfBuffer } = await api.post(API_PDF_GENERATORS.GENERATE_PDF, { content, logoType }, {
      responseType: 'arraybuffer',
      headers: {
        Accept: 'application/pdf'
      }
    })
    return { data: await fileToBase64(bufferToBlobPdf(pdfBuffer)) }
  }

  async generateQiContent ({ id, translate }) {
    const [{ data: dataQi }] = await Promise.all([
      ServiceQi.getDynamic(id, {
        select: [
          'id',
          'new { part.partNumber, part.partDescription} as part',
          'new { condition.title } as condition',
          'new { supplierCompany.name } as supplierCompany',
          'new { tracebilityCompany.name } as tracebilityCompany',
          'new { tagInfoCompany.name } as tagInfoCompany',
          'new { release.title } as release',
          'new { employee.name } as employee',
          'tagDate', 'partSN', 'qty', 'awb', 'mfgDate',
          'remark', 'nonIncident', 'technicalReport', 'releaseApproved',
          'packagingIn', 'packagingOut',
          'entryDate', 'partVisualCheck', 'weightDimensions'
        ].join(',')
      })
    ])
    const content = await generateQi({ qi: dataQi, translate })
    return { content }
  }
  async generateQi ({ id, translate, logoKey }) {
    const { content } = await this.generateQiContent({ id, translate })
    return this.generatePdf({ content, logoKey })
  }

  async _generateInvoice3Content ({ id, translate }) {
    const [{ data: content }, { data: invoice }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.INVOICE_3),
      ServiceInvoice.getDynamic(id, {
        select: select(
          'id', 'rfqid', 'invoiceNumber', 'invoiceDate', 'dueDate',
          'po', 'remark', 'aogCharges', 'bankFees', 'shippingCharges',
          'new { vat.vatValue } as vat', 'totalPackages',
          'new { invoiceType.remarks } as invoiceType'
        )
      })
    ])
    const [{ data: rfq }, { data: { value: rfq3s } }] = await Promise.all([
      ServiceRFQs.getByRFQID(invoice.rfqid, {
        select: select(
          'id', 'rfqid',
          'new { paymentTerm.title } as paymentTerm',
          `new { customerCompany.name } as customerCompany`,
          `new {
              customerCompanyDetail.city,
              customerCompanyDetail.state,
              customerCompanyDetail.zipCode,
              customerCompanyDetail.street,
              customerCompanyDetail.country,
              customerCompanyDetail.vatNumber
            } as customerCompanyDetail`,
          `new { customerContact.name, customerContact.phone } as customerContact`,
          `new { shipToCompany.name } as shipToCompany`,
          `new {
              shipToCompanyDetail.city,
              shipToCompanyDetail.state,
              shipToCompanyDetail.zipCode,
              shipToCompanyDetail.street,
              shipToCompanyDetail.country,
              shipToCompanyDetail.vatNumber
          } as shipToCompanyDetail`,
          `new { shipToContact.name, shipToContact.phone } as shipToContact`
        )
      }),
      ServiceInvoice.getInvoiceDetailsRFQ3({
        invoiceNumber: invoice.invoiceNumber,
        rfqid: invoice.rfqid,
        params: {
          select: select(
            'id', 'qty', 'partSN', 'price',
            'new { part.partNumber, part.partDescription } as part',
            'new { condition.title } as condition',
            'new { currency.title } as currency'
          )
        }
      })
    ])
    const pdfContent = await generateInvoice3({ invoice, rfq, rfq3s, content, translate })
    return { content: pdfContent }
  }
  async _generateInvoice4Content ({ id, translate }) {
    const [{ data: content }, { data: invoice }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.INVOICE_4),
      ServiceInvoice.getDynamic(id, {
        select: select(
          'id', 'rfqid', 'invoiceNumber', 'invoiceDate',
          'awb', 'totalPackages',
          'po', 'remark', 'aogCharges', 'bankFees', 'shippingCharges',
          'new { vat.vatValue } as vat',
          'new { countryOrigin.title } as countryOrigin',
          'new { invoiceType.remarks } as invoiceType',
          `new {
             new {
              rfq.customerForwarder.accountNumber,
              new { rfq.customerForwarder.shipper.shipperName } as shipper
             } as customerForwarder
           } as rfq`
        )
      })
    ])
    const [{ data: rfq }, { data: { value: rfq3s } }] = await Promise.all([
      ServiceRFQs.getByRFQID(invoice.rfqid, {
        select: select(
          'id', 'rfqid',
          `new { shipToCompany.name } as shipToCompany`,
          `new {
              shipToCompanyDetail.city,
              shipToCompanyDetail.state,
              shipToCompanyDetail.zipCode,
              shipToCompanyDetail.street,
              shipToCompanyDetail.country,
              shipToCompanyDetail.vatNumber
          } as shipToCompanyDetail`,
          `new { shipToContact.name, shipToContact.phone } as shipToContact`
        )
      }),
      ServiceInvoice.getInvoiceDetailsRFQ3({
        invoiceNumber: invoice.invoiceNumber,
        rfqid: invoice.rfqid,
        params: {
          select: select(
            'id', 'qty', 'partSN', 'price', 'tagDate',
            'new { part.partNumber, part.partDescription } as part',
            'new { condition.title } as condition',
            'new { currency.title } as currency',
            'new { tagInfoCompany.name } as tagInfoCompany',
            'new { release.title } as release'
          )
        }
      })
    ])
    const pdfContent = await generateInvoice4({ invoice, rfq, rfq3s, content, translate })
    return { content: pdfContent }
  }
  async _generateInvoice5Content ({ id, translate }) {
    const [{ data: content }, { data: invoice }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.INVOICE_5),
      ServiceInvoice.getDynamic(id, {
        select: select(
          'id', 'rfqid', 'invoiceNumber', 'invoiceDate', 'dueDate',
          'po', 'remark', 'aogCharges', 'bankFees', 'shippingCharges',
          'new { vat.vatValue } as vat'
        )
      })
    ])
    const [{ data: rfq }, { data: { value: rfq3s } }] = await Promise.all([
      ServiceRFQs.getByRFQID(invoice.rfqid, {
        select: select(
          'new { paymentTerm.title } as paymentTerm',
          `new { customerCompany.name } as customerCompany`,
          `new {
              customerCompanyDetail.city,
              customerCompanyDetail.state,
              customerCompanyDetail.zipCode,
              customerCompanyDetail.street,
              customerCompanyDetail.country,
              customerCompanyDetail.vatNumber
            } as customerCompanyDetail`,
          `new { customerContact.name, customerContact.phone } as customerContact`,
          `new { shipToCompany.name } as shipToCompany`,
          `new {
              shipToCompanyDetail.city,
              shipToCompanyDetail.state,
              shipToCompanyDetail.zipCode,
              shipToCompanyDetail.street,
              shipToCompanyDetail.country,
              shipToCompanyDetail.vatNumber
          } as shipToCompanyDetail`,
          `new { shipToContact.name, shipToContact.phone } as shipToContact`
        )
      }),
      ServiceInvoice.getInvoiceDetailsRFQ3({
        invoiceNumber: invoice.invoiceNumber,
        rfqid: invoice.rfqid,
        params: {
          select: select(
            'id', 'qty', 'partSN', 'price',
            'new { part.partNumber, part.partDescription } as part',
            'new { condition.title } as condition',
            'new { currency.title } as currency'
          )
        }
      })
    ])
    const pdfContent = await generateInvoice5({ invoice, rfq, rfq3s, content, translate })
    return { content: pdfContent }
  }
  async _generateInvoice7Content ({ id, translate }) {
    const [{ data: content }, { data: invoice }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.INVOICE_7),
      ServiceInvoice.getDynamic(id, {
        select: select(
          'id', 'rfqid', 'invoiceNumber', 'invoiceDate', 'dueDate',
          'po', 'remark', 'aogCharges', 'bankFees', 'shippingCharges',
          'new { vat.vatValue } as vat'
        )
      })
    ])
    const [{ data: rfq }, { data: { value: rfq3s } }] = await Promise.all([
      ServiceRFQs.getByRFQID(invoice.rfqid, {
        select: select(
          'new { paymentTerm.title } as paymentTerm',
          `new { customerCompany.name } as customerCompany`,
          `new {
              customerCompanyDetail.city,
              customerCompanyDetail.state,
              customerCompanyDetail.zipCode,
              customerCompanyDetail.street,
              customerCompanyDetail.country,
              customerCompanyDetail.vatNumber
            } as customerCompanyDetail`,
          `new { customerContact.name, customerContact.phone } as customerContact`,
          `new { shipToCompany.name } as shipToCompany`,
          `new {
              shipToCompanyDetail.city,
              shipToCompanyDetail.state,
              shipToCompanyDetail.zipCode,
              shipToCompanyDetail.street,
              shipToCompanyDetail.country,
              shipToCompanyDetail.vatNumber
          } as shipToCompanyDetail`,
          `new { shipToContact.name, shipToContact.phone } as shipToContact`
        )
      }),
      ServiceInvoice.getInvoiceDetailsRFQ3({
        invoiceNumber: invoice.invoiceNumber,
        rfqid: invoice.rfqid,
        params: {
          select: select(
            'id', 'qty', 'partSN', 'price',
            'new { part.partNumber, part.partDescription } as part',
            'new { condition.title } as condition',
            'new { currency.title } as currency'
          )
        }
      })
    ])

    const pdfContent = await generateInvoice7({ invoice, rfq, rfq3s, content, translate })
    return { content: pdfContent }
  }
  async _generateInvoice8Content ({ id, translate }) {
    const [{ data: content }, { data: invoice }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.INVOICE_8),
      ServiceInvoice.getDynamic(id, {
        select: select(
          'id', 'rfqid', 'invoiceNumber', 'invoiceDate',
          'po', 'remark', 'aogCharges', 'bankFees', 'shippingCharges',
          'new { vat.vatValue } as vat'
        )
      })
    ])
    const [{ data: rfq }, { data: { value: rfq3s } }] = await Promise.all([
      ServiceRFQs.getByRFQID(invoice.rfqid, {
        select: select(
          `new { customerCompany.name } as customerCompany`,
          `new {
              customerCompanyDetail.city,
              customerCompanyDetail.state,
              customerCompanyDetail.zipCode,
              customerCompanyDetail.street,
              customerCompanyDetail.country,
              customerCompanyDetail.vatNumber
            } as customerCompanyDetail`,
          `new { customerContact.name, customerContact.phone } as customerContact`
        )
      }),
      ServiceInvoice.getInvoiceDetailsRFQ3({
        invoiceNumber: invoice.invoiceNumber,
        rfqid: invoice.rfqid,
        params: {
          select: select(
            'id', 'qty', 'partSN', 'price',
            'new { part.partNumber, part.partDescription } as part',
            'new { condition.title } as condition',
            'new { currency.title } as currency'
          )
        }
      })
    ])

    const pdfContent = await generateInvoice8({ invoice, rfq, rfq3s, content, translate })
    return { content: pdfContent }
  }
  async _generateInvoice9Content ({ id, translate }) {
    const [{ data: content }, { data: invoice }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.INVOICE_9),
      ServiceInvoice.getDynamic(id, {
        select: select(
          'id', 'rfqid', 'invoiceNumber', 'invoiceDate',
          'awb', 'totalPackages',
          'po', 'remark',
          'new { vat.vatValue } as vat',
          'new { countryOrigin.title } as countryOrigin',
          `new {
             new {
              rfq.customerForwarder.accountNumber,
              new { rfq.customerForwarder.shipper.shipperName } as shipper
             } as customerForwarder
           } as rfq`
        )
      })
    ])
    const [{ data: rfq }, { data: { value: rfq3s } }] = await Promise.all([
      ServiceRFQs.getByRFQID(invoice.rfqid, {
        select: select(
          `new { shipToCompany.name } as shipToCompany`,
          `new {
              shipToCompanyDetail.city,
              shipToCompanyDetail.state,
              shipToCompanyDetail.zipCode,
              shipToCompanyDetail.street,
              shipToCompanyDetail.country,
              shipToCompanyDetail.vatNumber
          } as shipToCompanyDetail`,
          `new { shipToContact.name, shipToContact.phone } as shipToContact`
        )
      }),
      ServiceInvoice.getInvoiceDetailsRFQ3({
        invoiceNumber: invoice.invoiceNumber,
        rfqid: invoice.rfqid,
        params: {
          select: select(
            'id', 'qty', 'partSN', 'tagDate',
            'new { part.partNumber, part.partDescription } as part',
            'new { condition.title } as condition',
            'new { tagInfoCompany.name } as tagInfoCompany',
            'new { release.title } as release'
          )
        }
      })
    ])
    const pdfContent = await generateInvoice9({ invoice, rfq, rfq3s, content, translate })
    return { content: pdfContent }
  }
  async _generateInvoice10Content ({ id, translate }) {
    const [{ data: content }, { data: invoice }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.INVOICE_10),
      ServiceInvoice.getDynamic(id, {
        select: select(
          'id', 'rfqid', 'invoiceNumber', 'invoiceDate',
          'awb', 'totalPackages',
          'po', 'remark', 'aogCharges', 'bankFees', 'shippingCharges',
          'new { vat.vatValue } as vat',
          'new { countryOrigin.title } as countryOrigin',
          `new {
             new {
              rfq.customerForwarder.accountNumber,
              new { rfq.customerForwarder.shipper.shipperName } as shipper
             } as customerForwarder
           } as rfq`
        )
      })
    ])
    const [{ data: rfq }, { data: { value: rfq3s } }] = await Promise.all([
      ServiceRFQs.getByRFQID(invoice.rfqid, {
        select: select(
          `new { shipToCompany.name } as shipToCompany`,
          `new {
              shipToCompanyDetail.city,
              shipToCompanyDetail.state,
              shipToCompanyDetail.zipCode,
              shipToCompanyDetail.street,
              shipToCompanyDetail.country,
              shipToCompanyDetail.vatNumber
          } as shipToCompanyDetail`,
          `new { shipToContact.name, shipToContact.phone } as shipToContact`
        )
      }),
      ServiceInvoice.getInvoiceDetailsRFQ3({
        invoiceNumber: invoice.invoiceNumber,
        rfqid: invoice.rfqid,
        params: {
          select: select(
            'id', 'qty', 'partSN', 'price', 'tagDate',
            'new { part.partNumber, part.partDescription } as part',
            'new { condition.title } as condition',
            'new { currency.title } as currency',
            'new { tagInfoCompany.name } as tagInfoCompany',
            'new { release.title } as release'
          )
        }
      })
    ])
    const pdfContent = await generateInvoice10({ invoice, rfq, rfq3s, content, translate })
    return { content: pdfContent }
  }

  async generateExchangeAgreementContent ({ id, translate }) {
    const [{ data: content }, { data: rfq3 }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.EXCHANGE_AGREEMENT),
      ServiceRFQ3s.getDynamic(id, {
        select: select(
          `new {
            rfq.po,
            rfq.poDate,
            new { rfq.customerContact.name, rfq.customerContact.phone, rfq.customerContact.email } as customerContact,
            new { rfq.customerCompany.name } as customerCompany,
            new { rfq.baxEmployee.name } as baxEmployee,
            new { rfq.customerCompanyDetail.street, rfq.customerCompanyDetail.city, rfq.customerCompanyDetail.country, rfq.customerCompanyDetail.zipCode } as customerCompanyDetail
          } as rfq`,
          'new { part.partNumber, part.partDescription } as part',
          'new { exchangeCondition.title } as exchangeCondition',
          'new { currency.title } as currency',
          'new { exchangeCoreValueCurrency.title } as exchangeCoreValueCurrency',
          'new { condition.title } as condition',
          'quoteDate', 'partSN', 'remarks', 'price',
          'exchangeCoreValue', 'coreDueDate'
        )
      })
    ])
    const pdfContent = await generateExchangeAgreement({ rfq3, content, translate })
    return { content: pdfContent }
  }
  async generateExchangeAgreement ({ id, translate, logoKey }) {
    const { content } = await this.generateExchangeAgreementContent({ id, translate })
    return this.generatePdf({ content, logoKey })
  }

  async generateInvoiceContent ({ id, translate, invoiceTypeID }) {
    switch (invoiceTypeID) {
      case 3:
        return this._generateInvoice3Content({ id, translate })
      case 4:
        return this._generateInvoice4Content({ id, translate })
      case 5:
        return this._generateInvoice5Content({ id, translate })
      case 7:
        return this._generateInvoice7Content({ id, translate })
      case 8:
        return this._generateInvoice8Content({ id, translate })
      case 9:
        return this._generateInvoice9Content({ id, translate })
      case 10:
        return this._generateInvoice10Content({ id, translate })
      default:
        return this._generateInvoice3Content({ id, translate })
    }
  }
  async generateInvoice ({ id, translate, invoiceTypeID, logoKey }) {
    const { content } = await this.generateInvoiceContent({ id, invoiceTypeID, translate })

    return this.generatePdf({ content, logoKey })
  }

  async generateOrderAcknowledgmentContent ({ id, translate }) {
    const [{ data: content }, { data: rfq }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.ORDER_ACKNOWLEDGMENT),
      ServiceRFQs.getDynamic(id, {
        select: select(
          'id', 'rfqid', 'poDate', 'po',
          `new { customerCompany.name } as customerCompany`,
          `new {
              customerCompanyDetail.city,
              customerCompanyDetail.state,
              customerCompanyDetail.zipCode,
              customerCompanyDetail.street,
              customerCompanyDetail.country,
              customerCompanyDetail.vatNumber
            } as customerCompanyDetail`,
          `new { customerContact.name, customerContact.phone } as customerContact`
        )
      })
    ])
    const [{ data: { value: rfq3s } }] = await Promise.all([
      ServiceRFQ3s.getAllDynamic({
        filter: mergeFilters(
          makeFilter('rfqid', rfq.rfqid, equals),
          makeFilter('chosen', true, equals)
        ).join('&&'),
        select: select(
          'id', 'qty', 'partSN', 'price',
          'new { part.partNumber, part.partDescription } as part',
          'new { condition.title } as condition',
          'new { currency.title } as currency',
          'supplyDate'
        )
      })
    ])
    const pdfContent = await generateOrderAcknowledgment({ rfq, rfq3s, content, translate })
    return { content: pdfContent }
  }
  async generateOrderAcknowledgment ({ id, translate, logoKey }) {
    const { content } = await this.generateOrderAcknowledgmentContent({ id, translate })
    return this.generatePdf({ content, logoKey })
  }

  async generatePackingSlipContent ({ id, translate }) {
    const [{ data: content }, { data: rfq3 }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.ATA_106),
      ServiceRFQ3s.getDynamic(id, {
        select: select(
          'id', 'stockID', 'qty', 'partSN',
          `new {
            rfq.po,
            new { rfq.customerCompany.name } as customerCompany
          } as rfq`,
          `new { part.partNumber, part.partDescription } as part`,
          `new {
            new { stock.condition.title } as condition
          } as stock`,
          `new { tagInfoCompany.name } as tagInfoCompany`,
          `new { traceabilityCompany.name } as traceabilityCompany`
        )
      })
    ])
    const pdfContent = await generatePackingSlip({ rfq3, content, translate })
    return { content: pdfContent }
  }
  async generatePackingSlip ({ id, translate, logoKey }) {
    const { content } = await this.generatePackingSlipContent({ id, translate })
    return this.generatePdf({ content, logoKey })
  }

  async generatePoContent ({ id, salesOrderID, translate }) {
    const [{ data: content }, { data: rfq }, { data: salesOrder }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.PO),
      ServiceRFQs.getDynamic(id, {
        select: select('id', 'rfqid')
      }),
      ServiceSalesOrders.getDynamic(salesOrderID, {
        select: select(
          'id', 'supplierCompanyID', 'entryDate', 'awb', 'baxpo', 'remarks',
          `new { supplierCompany.name } as supplierCompany`,
          `new {
             supplierCompanyDetail.street,
             supplierCompanyDetail.city,
             supplierCompanyDetail.zipCode,
             supplierCompanyDetail.state,
             supplierCompanyDetail.country,
             supplierCompanyDetail.vatNumber
           } as supplierCompanyDetail`,
          `new { supplierCompanyContact.name, supplierCompanyContact.phone } as supplierCompanyContact`,

          `new { shipToCompany.name } as shipToCompany`,
          `new {
             shipToCompanyDetail.street,
             shipToCompanyDetail.city,
             shipToCompanyDetail.zipCode,
             shipToCompanyDetail.state,
             shipToCompanyDetail.country,
             shipToCompanyDetail.vatNumber
           } as shipToCompanyDetail`,
          `new { shipToCompanyContact.name, shipToCompanyContact.phone } as shipToCompanyContact`,
          `new { vat.vatValue } as vat`,
          `new {
          shipViaForwarder.accountNumber,
            new { shipViaForwarder.shipper.shipperName } as shipper
          } as shipViaForwarder`,
          `new {
            employee.name,
            new { employee.contact.phone, employee.contact.email } as contact
          } as employee`,
          `new { priority.title } as priority`
        )
      })
    ])
    const { data: { value: rfq2s } } = await ServiceRFQ2s.getAllDynamic({
      filter: mergeFilters(
        makeFilter('rfqid', rfq.rfqid, equals),
        makeFilter('supplierCompanyID', salesOrder.supplierCompanyID, equals),
        makeFilter('chosen', true, equals)
      ).join('&&'),
      select: select(
        'id', 'qty', 'partSN', 'price', 'estimatedDeliveryDate', 'remarks',
        `new { part.partNumber, part.partDescription } as part`,
        `new { condition.title } as condition`,
        `new { currency.title } as currency`,
        `new { paymentTerm.title } as paymentTerm`,
        `new { supplierContact.name, supplierContact.phone } as supplierContact`
      )
    })
    const pdfContent = await generatePo({ salesOrder, rfq2s, content, translate })
    return { content: pdfContent }
  }
  async generatePo ({ id, salesOrderID, translate, logoKey }) {
    const { content } = await this.generatePoContent({ id, salesOrderID, translate })
    return this.generatePdf({ content, logoKey })
  }

  async generateProformaShipSupplierContent ({ id, salesOrderID, translate }) {
    const [{ data: content }, { data: rfq }, { data: salesOrder }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.PROFORMA_SHIP_SUPPLIER),
      ServiceRFQs.getDynamic(id, {
        select: select('id', 'rfqid')
      }),
      ServiceSalesOrders.getDynamic(salesOrderID, {
        select: select(
          'id', 'supplierCompanyID', 'entryDate', 'awb', 'noOfBoxes',
          'shippingPorformaNumber', 'baxpo', 'remarks', 'paymentChequeNo',
          'new { supplierCompany.name } as supplierCompany',
          `new {
            supplierCompanyDetail.street,
            supplierCompanyDetail.city,
            supplierCompanyDetail.zipCode,
            supplierCompanyDetail.state,
            supplierCompanyDetail.country,
            supplierCompanyDetail.vatNumber
          } as supplierCompanyDetail`,
          `new {
            supplierCompanyContact.name,
            supplierCompanyContact.phone
          } as supplierCompanyContact`,

          `new {
            shipViaForwarder.accountNumber,
            new { shipViaForwarder.shipper.shipperName } as shipper
          } as shipViaForwarder`,
          `new { vat.vatValue } as vat`,
          `new { countryOrigin.title } as countryOrigin`,
          `new {
            employee.name,
            new { employee.contact.phone, employee.contact.email } as contact
          } as employee`
        )
      })
    ])
    const { data: { value: rfq2s } } = await ServiceRFQ2s.getAllDynamic({
      filter: mergeFilters(
        makeFilter('rfqid', rfq.rfqid, equals),
        makeFilter('supplierCompanyID', salesOrder.supplierCompanyID, equals),
        makeFilter('chosen', true, equals)
      ).join('&&'),
      select: select(
        'id', 'qty', 'partSN', 'price', 'remarks',
        `new { part.partNumber, part.partDescription } as part`,
        `new { condition.title } as condition`,
        `new { currency.title } as currency`,
        `new { supplierContact.name, supplierContact.phone } as supplierContact`
      )
    })
    const pdfContent = await generateProformaShipSupplier({ salesOrder, rfq2s, content, translate })
    return { content: pdfContent }
  }
  async generateProformaShipSupplier ({ id, salesOrderID, translate, logoKey }) {
    const { content } = await this.generateProformaShipSupplierContent({ id, salesOrderID, translate })
    return this.generatePdf({ content, logoKey })
  }

  async generateQuoteMultiPartsContent ({ id, partIDs, translate }) {
    const [{ data: content }, { data: rfq }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.QUOTE_MULTI_PARTS),
      ServiceRFQs.getDynamic(id, {
        select: select('id', 'rfqid', 'observation', 'entryDate',
          'new { customerCompany.name } as customerCompany',
          'new { customerContact.name } as customerContact',
          `new {
            baxEmployee.name,
            new { baxEmployee.contact.phone, baxEmployee.contact.email, baxEmployee.contact.phone2 } as contact
          } as baxEmployee`
        )
      })
    ])
    const { data: { value: rfq3s } } = await ServiceRFQ3s.getAllDynamic({
      filter: mergeFilters(
        makeFilter('rfqid', rfq.rfqid, equals),
        makeFilter('partID', partIDs, decoratorValueArray(equals))
      ).join('&&'),
      select: select(
        'id', 'qty', 'partID', 'price', 'remarks', 'supplyDate', 'tagDate',
        'new { part.partNumber, part.partDescription } as part',
        'new { condition.title } as condition',
        'new { currency.title } as currency',
        'new { tagInfoCompany.name } as tagInfoCompany',
        'new { release.title } as release'
      )
    })
    const pdfContent = await generateQuoteMultiParts({ rfq, rfq3s, content, translate })
    return { content: pdfContent }
  }
  async generateQuoteMultiParts ({ id, partIDs, translate, logoKey }) {
    const { content } = await this.generateQuoteMultiPartsContent({ id, partIDs, translate })
    return this.generatePdf({ content, logoKey })
  }

  async generateTdContent ({ id, translate }) {
    const [{ data: content }, { data: rfq3 }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.TD_REPORT),
      ServiceRFQ3s.getDynamic(id, {
        select: select(
          'id', 'rfqid', 'partID', 'partSN',
          `new {
            rfq.po,
            rfq.poDate,
            new { rfq.customerCompany.name,  } as customerCompany,
            new { rfq.customerContact.name,  } as customerContact
          } as rfq`
        )
      })
    ])
    const [{ data: { value: rfq2s } }, { data: { value: repairTearDowns } }, { data: { value: replacementParts } }] = await Promise.all([
      ServiceRFQ2s.getAllDynamic({
        filter: mergeFilters(
          makeFilter('rfqid', rfq3.rfqid, equals),
          makeFilter('partID', rfq3.partID, equals),
          makeFilter('partSN', rfq3.partSN, equals)
        ).join('&&'),
        select: select('id', 'reasonForRemoval'),
        take: 1
      }),
      ServiceRepairTearDowns.getAllDynamic({
        filter: mergeFilters(
          makeFilter('rfqid', rfq3.rfqid, equals),
          makeFilter('partID', rfq3.partID, equals),
          makeFilter('partSN', rfq3.partSN, equals)
        ).join('&&'),
        select: select(
          'id', 'baxReceiveDate', 'partSN', 'rsFinding', 'leadTime', 'inspectionFee', 'baxTotalCost',
          'baxLaborCost', 'baxPartsCost', 'rsLaborCost', 'rsPartsCost', 'rsTotalCost', 'remarks',
          `new { part.partNumber, part.partDescription } as part`,
          `new { typeOfOperation.title } as typeOfOperation`,
          `new { baxCurrency.title } as baxCurrency`
        ),
        take: 1
      }),
      ServiceReplacementParts.getAllDynamic({
        filter: mergeFilters(
          makeFilter('rfqid', rfq3.rfqid, equals),
          makeFilter('partID', rfq3.partID, equals)
        ).join('&&'),
        select: select(
          'id', 'partNumber', 'partDescription', 'qty'
        )
      })
    ])
    const rfq2 = rfq2s[0]
    const repairReport = repairTearDowns[0]
    const pdfContent = await generateTd({ rfq2, rfq3, repairReport, replacementParts, content, translate })
    return { content: pdfContent }
  }
  async generateTd ({ id, translate, logoKey }) {
    const { content } = await this.generateTdContent({ id, translate })
    return this.generatePdf({ content, logoKey })
  }

  async generateWoContent ({ id, salesOrderID, translate }) {
    const [{ data: content }, { data: rfq }, { data: salesOrder }] = await Promise.all([
      ServiceContents.getByContentKey(CONTENT_KEYS.WO),
      ServiceRFQs.getDynamic(id, {
        select: select('id', 'rfqid')
      }),
      ServiceSalesOrders.getDynamic(salesOrderID, {
        select: select(
          'id', 'entryDate', 'supplierCompanyID', 'awb', 'baxpo', 'remarks',
          `new { supplierCompany.name } as supplierCompany`,
          `new {
             supplierCompanyDetail.street,
             supplierCompanyDetail.city,
             supplierCompanyDetail.zipCode,
             supplierCompanyDetail.state,
             supplierCompanyDetail.country,
             supplierCompanyDetail.vatNumber
           } as supplierCompanyDetail`,
          `new { supplierCompanyContact.name, supplierCompanyContact.phone } as supplierCompanyContact`,

          `new { shipToCompany.name } as shipToCompany`,
          `new {
             shipToCompanyDetail.street,
             shipToCompanyDetail.city,
             shipToCompanyDetail.zipCode,
             shipToCompanyDetail.state,
             shipToCompanyDetail.country,
             shipToCompanyDetail.vatNumber
           } as shipToCompanyDetail`,
          `new { shipToCompanyContact.name, shipToCompanyContact.phone } as shipToCompanyContact`,

          `new { vat.vatValue } as vat`,
          `new {
          shipViaForwarder.accountNumber,
            new { shipViaForwarder.shipper.shipperName } as shipper
          } as shipViaForwarder`,

          `new {
            employee.name,
            new { employee.contact.phone, employee.contact.email } as contact
          } as employee`,
          `new { priority.title } as priority`
        )
      })
    ])
    const { data: { value: rfq2s } } = await ServiceRFQ2s.getAllDynamic({
      filter: mergeFilters(
        makeFilter('rfqid', rfq.rfqid, equals),
        makeFilter('supplierCompanyID', salesOrder.supplierCompanyID, equals),
        makeFilter('chosen', true, equals)
      ).join('&&'),
      select: select(
        'id', 'qty', 'partSN', 'price', 'estimatedDeliveryDate', 'remarks',
        `new { part.partNumber, part.partDescription } as part`,
        `new { condition.title } as condition`,
        `new { currency.title } as currency`,
        `new { paymentTerm.title } as paymentTerm`,
        `new { supplierContact.name, supplierContact.phone } as supplierContact`,
        `new { typeOfOperationCondition.title } as typeOfOperationCondition`,
        'reasonForRemoval',
        'warrantyRequested'
      )
    })
    const pdfContent = await generateWo({ salesOrder, rfq2s, content, translate })

    return { content: pdfContent }
  }
  async generateWo ({ id, salesOrderID, translate, logoKey }) {
    const { content } = await this.generateWoContent({ id, salesOrderID, translate })
    return this.generatePdf({ content, logoKey })
  }
}

export const ServicePdf = new Service()
